// Pacote de escolhas (Pedro, 15/10/2026 — nasceu como «Contar preparos», caso Personal Chef): o produto tem N itens
// inclusos no preço, aceita até M e cobra E por item a mais. Cada escolha conta o peso da opção (½, 1 ou 2; ver
// `optionWeight`). Acima de N, os extras são arredondados para cima: meio item a mais já conta 1 extra inteiro
// (decisão do Pedro). A mesma conta vale no link, no PDV e no servidor; quem trava (link) ou só avisa (PDV) é quem chama.
// Os textos usam o nome que a loja dá aos itens (`unitName`, no plural; padrão «itens») e não têm gênero nem singular
// (Pedro, 15/10/2026): «7 marmitas no pacote», «Marque mais 2 marmitas», «Marmitas: 8½ (7 do pacote + 2 extras)».
//
// «Até N no preço» (15/10/2026, caso Meu macarrão: «o pimentão entrou de graça… dessa cebola cobra 2,50. Tudo na
// mesma lista»): `required: false` deixa o cliente marcar de 0 a N pelo preço do produto e cobra o extra só acima de
// N; `max` null = sem limite; e o grupo com `inPackage: false` (o tipo de macarrão, o molho) fica fora da conta.
// Sem esses campos, o pacote é o de antes: completar N é obrigatório no link, há um máximo e todas as listas contam.
//
// «Cada lista conta separado» (`porLista`, Pedro 28/10/2026, caso Açaí beat: «Copo 1: 1º adicional grátis, 2º adicional
// grátis, 3º em diante + 2 reais cada / Copo 2: igual»): N no preço, o extra, o máximo e o obrigatório valem para CADA
// lista que conta, e cada uma tem a sua conta. Os extras do item são a soma dos extras das listas. Sem a marca, as listas
// que contam somam num total só, como antes.
import { round2 } from "./pricing";
import { countPreparos, countPreparosPorGrupo, formatQtd, type OptionGroup } from "./validateOptions";

/** Configuração do produto (`products.prep_count`). Desligada ou incompleta = o produto funciona como sempre. */
export interface PrepCountConfig {
  enabled?: boolean | null;
  included?: number | null;
  /** Máximo do total; null/ausente = sem limite. */
  max?: number | null;
  extraPrice?: number | null;
  /** Como a loja chama os itens, no plural («preparos», «marmitas»). Vazio = «itens». */
  unitName?: string | null;
  /** false = «até N no preço»: abaixo de N não falta nada. Ausente/true = completar N é obrigatório no link. */
  required?: boolean | null;
  /** true = cada lista que conta tem a própria conta (N, extra, máximo e obrigatório por lista). */
  porLista?: boolean | null;
}
export type PrepCountLigado = PrepCountConfig & { enabled: true; included: number };

export const NOME_DOS_ITENS_PADRAO = "itens";
export const NOME_DOS_ITENS_MAX = 20;

/** O nome dos itens como a loja gravou, limpo: espaços colapsados, até 20 caracteres; vazio = «itens». */
export function nomeDosItens(raw: string | null | undefined): string {
  const limpo = String(raw ?? "").replace(/\s+/g, " ").trim().slice(0, NOME_DOS_ITENS_MAX).trim();
  return limpo || NOME_DOS_ITENS_PADRAO;
}

/** No meio da frase: «Marmitas» → «marmitas» (sigla como «PFs» fica como está). */
function noMeio(palavra: string): string {
  return palavra.length > 1 && palavra[1] === palavra[1]!.toLowerCase() ? palavra[0]!.toLowerCase() + palavra.slice(1) : palavra;
}

/** No começo da linha do pedido: «marmitas» → «Marmitas». */
function noComeco(palavra: string): string {
  return palavra ? palavra[0]!.toUpperCase() + palavra.slice(1) : palavra;
}

/** A configuração vale: ligada, N ≥ 0, preço do extra ≥ 0 e, com máximo, M ≥ N. */
export function prepCountOn(cfg: PrepCountConfig | null | undefined): cfg is PrepCountLigado {
  if (!cfg || cfg.enabled !== true) return false;
  const n = Number(cfg.included);
  const e = Number(cfg.extraPrice ?? 0);
  if (!(Number.isFinite(n) && n >= 0 && Number.isFinite(e) && e >= 0)) return false;
  if (cfg.max == null) return true;
  const m = Number(cfg.max);
  return Number.isFinite(m) && m >= n;
}

export interface PrepCount {
  /** Total escolhido (X), em meios. */
  total: number;
  included: number;
  /** null = sem limite. */
  max: number | null;
  extraPrice: number;
  /** Extras cobrados: arredondar para cima (X − N) quando X > N. */
  extras: number;
  /** extras × preço do extra, por unidade do produto. */
  extrasValue: number;
  /** Quanto falta para chegar a N (0 quando já chegou, e sempre 0 no «até N no preço»). */
  missing: number;
  /** Passou do máximo M (nunca, sem limite). */
  overMax: boolean;
  /** false = «até N no preço». */
  required: boolean;
  /** Como a loja chama os itens, já limpo e no meio da frase («preparos»). */
  unitName: string;
  /** «Cada lista conta separado». */
  porLista: boolean;
  /** Com `porLista`, a conta de cada lista que conta (na ordem do produto); os campos acima são a soma delas. */
  listas?: PrepCountDaLista[];
}

/** A conta de uma lista, no pacote que conta cada lista separado. */
export type PrepCountDaLista = PrepCount & { groupIndex: number; nome: string };

export function prepCount(cfg: PrepCountConfig, total: number): PrepCount {
  const included = Number(cfg.included) || 0;
  const max = cfg.max == null ? null : Number(cfg.max) || 0;
  const extraPrice = Number(cfg.extraPrice) || 0;
  const required = cfg.required !== false;
  // Os pesos são ½, 1 e 2: a soma é exata em meios. O epsilon só protege de lixo de ponto flutuante.
  const extras = total > included ? Math.ceil(total - included - 1e-9) : 0;
  return {
    total,
    included,
    max,
    extraPrice,
    extras,
    extrasValue: round2(extras * extraPrice),
    missing: required ? Math.max(0, included - total) : 0,
    overMax: max != null && total > max + 1e-9,
    required,
    unitName: noMeio(nomeDosItens(cfg.unitName)),
    porLista: cfg.porLista === true,
  };
}

/**
 * A conta do item: no pacote de sempre, o total das listas que contam; com `porLista`, a conta de cada lista e, nos
 * campos de sempre, a soma (extras, valor, o que falta e se alguma passou do máximo). Quem cobra e quem trava olha a
 * soma; quem mostra usa `listas`.
 */
export function prepCountDoItem(
  cfg: PrepCountConfig,
  groups: OptionGroup[] | null | undefined,
  selected: Array<{ groupName: string; optionName: string; groupIndex?: number | null }> | null | undefined,
): PrepCount {
  if (cfg.porLista !== true) return prepCount(cfg, countPreparos(groups, selected));
  const porGrupo = countPreparosPorGrupo(groups, selected);
  const listas: PrepCountDaLista[] = (groups || []).flatMap((g, gi) =>
    g?.inPackage === false ? [] : [{ ...prepCount(cfg, porGrupo[gi] ?? 0), groupIndex: gi, nome: String(g?.name ?? "").trim() || `Lista ${gi + 1}` }],
  );
  const base = prepCount(cfg, 0);
  const soma = (f: (l: PrepCountDaLista) => number) => listas.reduce((acc, l) => acc + f(l), 0);
  return {
    ...base,
    total: soma((l) => l.total),
    included: base.included * listas.length,
    max: base.max == null ? null : base.max * listas.length,
    extras: soma((l) => l.extras),
    extrasValue: round2(soma((l) => l.extrasValue)),
    missing: soma((l) => l.missing),
    overMax: listas.some((l) => l.overMax),
    listas,
  };
}

/** A conta da lista na posição `groupIndex` (só com `porLista`). */
export function prepCountDaLista(c: PrepCount | null | undefined, groupIndex: number): PrepCountDaLista | null {
  return c?.listas?.find((l) => l.groupIndex === groupIndex) ?? null;
}

/**
 * A conta do item, ou null quando o produto não tem o pacote (configuração desligada/incompleta, ou `ativo` false:
 * a loja sem o recurso — plano ou liberação vencida — vende o produto como sempre). Quem cobra soma `extrasValue` ao
 * valor das opções da unidade: vitrine, carrinho, PDV e servidor fazem a mesma soma.
 */
export function prepCountFor(
  groups: OptionGroup[] | null | undefined,
  cfg: PrepCountConfig | null | undefined,
  selected: Array<{ groupName: string; optionName: string; groupIndex?: number | null }> | null | undefined,
  ativo = true,
): PrepCount | null {
  if (!ativo || !prepCountOn(cfg)) return null;
  return prepCountDoItem(cfg, groups, selected);
}

/** Os grupos como a validação deve vê-los: no produto com o pacote, o grupo que conta nele não tem máximo próprio
 *  (o de fora do pacote segue com o dele). */
export function groupsForValidation<G extends OptionGroup>(groups: G[] | null | undefined, contando: boolean): G[] {
  const gs = groups || [];
  return contando ? gs.map((g) => (g.inPackage === false ? g : { ...g, maxSelections: null })) : gs;
}

/** 8.5 → "8½"; 0.5 → "½"; 7 → "7". */
export const formatPreparos = formatQtd;

/** «2 marmitas»; com 1 ou ½ só o número («1»), porque a loja só informa o plural. */
export function qtdComNome(n: number, unitName: string): string {
  return n > 1 + 1e-9 ? `${formatQtd(n)} ${unitName}` : formatQtd(n);
}

/** Botão e recusa abaixo dos inclusos: «Marque mais 2 marmitas» / «Marque mais 1». Com `porLista`, a 1ª lista que falta:
 *  «Marque mais 1 em Adicionais - Copo 2». */
export function prepFaltamTexto(c: Pick<PrepCount, "missing" | "unitName"> & { listas?: PrepCountDaLista[] }): string {
  const lista = c.listas?.find((l) => l.missing > 0);
  if (lista) return `Marque mais ${qtdComNome(lista.missing, lista.unitName)} em ${lista.nome}`;
  return `Marque mais ${qtdComNome(c.missing, c.unitName)}`;
}

/**
 * Linha do cartão do produto; o valor quem formata é a tela (`preco`):
 * - pacote: «7 marmitas no pacote · até 3 extras de R$ 35,00» (sem máximo: «· a partir de 8, R$ 35,00 cada»);
 * - «até N no preço»: «3 ingredientes no preço · a partir de 4, R$ 2,50 cada» (com máximo: «… · até 6»).
 * Sem gênero: «a partir de 4», não «do 4º».
 */
export function prepCardResumo(
  c: Pick<PrepCount, "included" | "max" | "unitName" | "extraPrice" | "required"> & { porLista?: boolean },
  preco: (valor: number) => string,
): { pacote: string; extras: string | null } {
  // «Cada lista conta separado»: a mesma linha, valendo para cada lista («2 adicionais no preço em cada lista · …»).
  if (c.porLista) {
    const r = prepCardResumo({ ...c, porLista: false }, preco);
    return { ...r, pacote: `${r.pacote} em cada lista` };
  }
  const cada = c.extraPrice > 0 ? `a partir de ${formatQtd(Math.floor(c.included) + 1)}, ${preco(c.extraPrice)} cada` : null;
  if (!c.required) {
    const ate = c.max != null ? `até ${formatQtd(c.max)}` : null;
    return { pacote: `${qtdComNome(c.included, c.unitName)} no preço`, extras: [cada, ate].filter(Boolean).join(" · ") || null };
  }
  if (c.max == null) return { pacote: `${qtdComNome(c.included, c.unitName)} no pacote`, extras: cada };
  const extras = c.max - c.included;
  const ateExtras = extras > 0 ? `até ${formatQtd(extras)} ${extras > 1 + 1e-9 ? "extras" : "extra"}` : null;
  return {
    pacote: `${qtdComNome(c.included, c.unitName)} no pacote`,
    extras: ateExtras && c.extraPrice > 0 ? `${ateExtras} de ${preco(c.extraPrice)}` : ateExtras,
  };
}

/** O nome nas linhas do pedido; retrato antigo (antes do nome, 15/10/2026) é todo da Personal Chef: «preparos». */
function nomeDoRetrato(unitName: string | null | undefined): string {
  return unitName == null ? "Preparos" : noComeco(nomeDosItens(unitName));
}

/**
 * A linha do item no pedido (mensagem do WhatsApp, cupom e painel):
 * «Marmitas: 8½ (7 do pacote + 2 extras)»; até os inclusos (abaixo só no PDV): «Marmitas: 5 de 7».
 * «Até N no preço» (`required: false`): «Ingredientes: 2 de 3 no preço» / «Ingredientes: 4 (3 no preço + 1 extra)».
 */
export function prepCountLine(
  c: Pick<PrepCount, "total" | "included" | "extras"> & { unitName?: string | null; required?: boolean | null },
  /** O começo da linha no lugar do nome dos itens (no `porLista`, o nome da lista). */
  rotulo?: string,
): string {
  return `${rotulo ?? nomeDoRetrato(c.unitName)}: ${prepContaTexto(c)}`;
}

/** A conta sem o nome: «8½ (7 do pacote + 2 extras)», «2 de 3 no preço». */
export function prepContaTexto(c: Pick<PrepCount, "total" | "included" | "extras"> & { required?: boolean | null }): string {
  const x = formatQtd(c.total);
  const noPreco = c.required === false;
  if (c.total <= c.included + 1e-9) return `${x} de ${formatQtd(c.included)}${noPreco ? " no preço" : ""}`;
  const extras = c.extras > 0 ? ` + ${c.extras} ${c.extras === 1 ? "extra" : "extras"}` : "";
  return `${x} (${formatQtd(c.included)} ${noPreco ? "no preço" : "do pacote"}${extras})`;
}

/** Um grupo de opções do item, com a conta da lista quando ela conta separado. */
export interface GrupoDoItem {
  titulo: string;
  /** «3 (2 no preço + 1 extra)»; null no grupo que não é lista do pacote por lista. */
  conta: string | null;
  /** O valor dos extras da lista, por unidade do produto. */
  extrasValue: number;
  opcoes: Array<{ nome: string; preco: number; count: number }>;
}

/**
 * «Cada lista conta separado» (28/10/2026): as opções do item agrupadas pela lista, com a conta da lista no título, para
 * a sacola, o PDV e a mensagem mostrarem cada adicional embaixo do seu copo (Pedro: «Como vai saber qual vai em qual
 * copo?»). A lista que conta e ficou sem escolha também aparece, com «0 de 2 no preço». A ordem é a das escolhas; as
 * listas vazias vão no fim. `quantidade` multiplica a contagem, como o resto da sacola.
 */
export function gruposComConta(
  opcoes: Array<{ groupName?: string | null; optionName: string; price?: number | string | null }> | null | undefined,
  c: { required?: boolean | null; extraPrice?: number; listas?: Array<Pick<PrepCount, "total" | "included" | "extras"> & { nome: string; extrasValue?: number }> | null } | null | undefined,
  quantidade = 1,
): GrupoDoItem[] {
  const listas = new Map((c?.listas ?? []).map((l) => [l.nome, l]));
  const grupos: GrupoDoItem[] = [];
  const porTitulo = new Map<string, GrupoDoItem>();
  const novo = (titulo: string): GrupoDoItem => {
    const l = listas.get(titulo);
    const g: GrupoDoItem = {
      titulo,
      conta: l ? prepContaTexto({ ...l, required: c?.required }) : null,
      extrasValue: l ? (l.extrasValue ?? round2(l.extras * (c?.extraPrice ?? 0))) : 0,
      opcoes: [],
    };
    porTitulo.set(titulo, g);
    grupos.push(g);
    return g;
  };
  for (const o of opcoes ?? []) {
    const titulo = String(o.groupName ?? "").trim();
    const g = porTitulo.get(titulo) ?? novo(titulo);
    const ja = g.opcoes.find((x) => x.nome === o.optionName);
    if (ja) ja.count += quantidade;
    else g.opcoes.push({ nome: o.optionName, preco: Number(o.price) || 0, count: quantidade });
  }
  for (const nome of listas.keys()) if (!porTitulo.has(nome)) novo(nome);
  return grupos;
}

/**
 * As linhas do item no pedido: uma só no pacote de sempre; com `porLista`, uma por lista, com o nome dela:
 * «Adicionais - Copo 1: 3 (2 no preço + 1 extra)». Serve para a conta ao vivo e para o retrato gravado.
 */
export function prepCountLines(
  c: Pick<PrepCount, "total" | "included" | "extras"> & {
    unitName?: string | null;
    required?: boolean | null;
    listas?: Array<Pick<PrepCount, "total" | "included" | "extras"> & { nome: string }> | null;
  },
): string[] {
  if (c.listas?.length) return c.listas.map((l) => prepCountLine({ ...l, unitName: c.unitName, required: c.required }, l.nome));
  return [prepCountLine(c)];
}

/** Recusa do servidor abaixo dos inclusos: «Marque mais 2 marmitas em "Marmita Semanal".» Com `porLista`, a lista:
 *  «Marque mais 1 em Adicionais - Copo 2 em "Combo".» */
export function prepRecusaFaltam(c: Pick<PrepCount, "missing" | "unitName"> & { listas?: PrepCountDaLista[] }, produto: string): string {
  return `${prepFaltamTexto(c)} em "${produto}".`;
}

/** Recusa do servidor acima do máximo: «"Marmita Semanal" aceita até 10 marmitas (você marcou 11½).» Com `porLista`, a
 *  1ª lista que passou: «Adicionais - Copo 1, em "Combo", aceita até 4 (você marcou 5).» */
export function prepRecusaPassou(c: Pick<PrepCount, "max" | "total" | "unitName"> & { listas?: PrepCountDaLista[] }, produto: string): string {
  const lista = c.listas?.find((l) => l.overMax);
  if (lista) return `${lista.nome}, em "${produto}", aceita até ${qtdComNome(lista.max ?? 0, lista.unitName)} (você marcou ${formatQtd(lista.total)}).`;
  return `"${produto}" aceita até ${qtdComNome(c.max ?? 0, c.unitName)} (você marcou ${formatQtd(c.total)}).`;
}

/** Retrato da conta gravado no item do pedido (`order_items.prep_count`). `required` só vai quando é «até N no
 *  preço» (false); retrato sem ele é do pacote obrigatório. */
export interface PrepCountSnapshot {
  total: number;
  included: number;
  max: number | null;
  extras: number;
  extraPrice: number;
  unitName: string;
  required?: false;
  /** «Cada lista conta separado»: a conta de cada lista; os campos acima são a soma. */
  listas?: PrepCountSnapshotDaLista[];
}

export interface PrepCountSnapshotDaLista {
  nome: string;
  total: number;
  included: number;
  max: number | null;
  extras: number;
}

export function prepCountSnapshot(c: PrepCount): PrepCountSnapshot {
  return {
    total: c.total,
    included: c.included,
    max: c.max,
    extras: c.extras,
    extraPrice: c.extraPrice,
    unitName: c.unitName,
    ...(c.required ? {} : { required: false as const }),
    ...(c.listas ? { listas: c.listas.map((l) => ({ nome: l.nome, total: l.total, included: l.included, max: l.max, extras: l.extras })) } : {}),
  };
}

/** O peso da opção como o cliente lê do lado dela: «conta ½», «conta 2» (1 não mostra nada). */
export function optionWeightLabel(weight: number): string | null {
  if (weight === 0.5) return "conta ½";
  if (weight === 2) return "conta 2";
  return null;
}
